import React from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Mail, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n";

export default function Contact({ content }) {
  const { t, lang } = useLang();
  const hours = lang === "en" ? content.hours_en : content.hours_es;
  const mapsQuery = encodeURIComponent(`${content.address}, ${content.city}`);

  return (
    <section id="contact" className="relative z-10 bg-[#0a0a0a] py-24 sm:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-14 max-w-2xl">
          <div className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary-red mb-4">/ {t.contact.kicker}</div>
          <h2 className="font-display uppercase text-4xl sm:text-6xl text-white leading-[0.95]">{t.contact.title}</h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="flex flex-col gap-6">
            <motion.a
              href={`tel:${content.phone.replace(/[^0-9]/g, "")}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group bg-primary-red p-8 flex items-center justify-between hover:bg-[#FF1E16] transition-colors"
              data-testid="contact-call"
            >
              <div>
                <div className="flex items-center gap-2 font-body font-bold uppercase text-white/80 text-sm tracking-widest">
                  <Phone size={16} /> {t.contact.callTitle}
                </div>
                <div className="font-display text-4xl sm:text-5xl text-white mt-2">{content.phone}</div>
              </div>
              <ArrowUpRight size={40} className="text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </motion.a>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="border border-white/10 p-7">
                <MapPin size={22} className="text-primary-red mb-3" />
                <div className="font-body font-bold uppercase text-white/60 text-xs tracking-widest mb-2">{t.contact.visit}</div>
                <div className="font-display text-2xl text-white leading-tight">{content.address}</div>
                <div className="font-body text-neutral-400">{content.city}</div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 mt-4 font-mono2 text-xs uppercase text-primary-red hover:underline"
                  data-testid="contact-directions"
                >
                  {t.contact.directions} <ArrowUpRight size={14} />
                </a>
              </div>

              <div className="border border-white/10 p-7">
                <Clock size={22} className="text-primary-red mb-3" />
                <div className="font-body font-bold uppercase text-white/60 text-xs tracking-widest mb-2">{t.contact.hoursTitle}</div>
                <div className="font-display text-2xl text-white leading-tight">{hours}</div>
                <a
                  href={`mailto:${content.email}`}
                  className="inline-flex items-center gap-1 mt-4 font-mono2 text-xs uppercase text-primary-red hover:underline"
                  data-testid="contact-email"
                >
                  <Mail size={14} /> {content.email}
                </a>
              </div>
            </div>
          </div>

          <div className="min-h-[360px] border border-white/10 overflow-hidden">
            <iframe
              title="JJ Truck Repair location"
              src={`https://maps.google.com/maps?q=${mapsQuery}&output=embed`}
              className="w-full h-full min-h-[360px] grayscale contrast-125"
              loading="lazy"
              style={{ border: 0 }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
